
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Divider,
  Button,
} from "@mui/material";
import FocusedFlowPage from "components/FocusedFlowPage";
import { useEffect, useRef, useState } from "react";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { usePatient } from "./PatientProvider";
import {
  checkInEncounterV2,
  createEncounter,
  fetchPatientById,
  updatePatient,
} from "utils/api";
import { HEALTH_CONDITION_OPTIONS, ALZHEIMERS_STATUS_OPTIONS } from "../../../constants/patientOptions";
import ChipSelect from "components/ChipSelect";
import { v4 as uuidv4 } from "uuid";
import { nowIso } from "utils/appDateTime";
import { buildEncounterCheckInV2Payload } from "../../../utils/checkInPayloads";
import { ENCOUNTER_TYPE } from "constants/encounterType";
import { CHECK_IN_STATUS } from "constants/checkInStatus";
import { toYesNoDisplayValue, toYesNoStoredValue } from "../../../utils/optionNormalizers";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const normalizeHistoryValues = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && typeof item !== "undefined" && String(item).trim() !== "");
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return normalizeHistoryValues(parsed);
      }
    } catch (error) {
      // Not JSON; fall through to comma-separated or single-value handling.
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const FamilyInfo = () => {
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [medicalHistoryOther, setMedicalHistoryOther] = useState("");
  const [alzheimersStatus, setAlzheimersStatus] = useState("");
  const [familyHistory, setFamilyHistory] = useState([]);
  const [familyHistoryOther, setFamilyHistoryOther] = useState("");
  const [smoking, setSmoking] = useState("");
  const [yearsSmoking, setYearsSmoking] = useState("");
  const [drinking, setDrinking] = useState("");
  const [drinksPerWeek, setDrinksPerWeek] = useState("");
  const [exercise, setExercise] = useState("");
  const [complete, setComplete] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReturningPatient, setIsReturningPatient] = useState(false);
  const { patientData, updatePatientData } = usePatient();
  const navigate = useNavigate();
  const location = useLocation();

  const hasLoadedInitialData = useRef(false);

  useEffect(() => {
    if (hasLoadedInitialData.current) return;
    hasLoadedInitialData.current = true;

    const loadAndPrefill = async () => {
      try {
        console.log("🔍 FamilyInfo - Location state:", location.state);
        console.log("🔍 FamilyInfo - Context patientData:", patientData);

        let source = patientData;

        const patientId = location.state?.patientId || patientData?.patientId;

        // Always fetch from API for existing patients to get latest data
        // This ensures prefilled data survives refresh
        if (patientId) {
          console.log("🔍 FamilyInfo - Fetching full patient from API:", patientId);
          try {
            const fetched = await fetchPatientById(patientId);
            if (fetched) {
              console.log("📊 FamilyInfo - API patient:", fetched);
              // Merge API data into context, preserving flow-specific fields
              const merged = { ...patientData, ...fetched };
              updatePatientData(merged);
              source = merged;

              // Check if patient has previous data (not first visit)
              // If they have filled out medical/family history before, they are returning
              const hasHistoryData =
                (fetched.medicalHistory && Array.isArray(fetched.medicalHistory) &&
                 fetched.medicalHistory.length > 0 && fetched.medicalHistory[0] !== "none") ||
                (fetched.familyHistory && Array.isArray(fetched.familyHistory) &&
                 fetched.familyHistory.length > 0 && fetched.familyHistory[0] !== "none") ||
                fetched.smoking !== null ||
                fetched.drinking !== null ||
                fetched.exercise !== null;

              setIsReturningPatient(hasHistoryData);
              console.log("🔍 FamilyInfo - Is returning patient:", hasHistoryData);
            }
          } catch (err) {
            console.error("FamilyInfo - API fetch failed, using context:", err);
          }
        }

        // Also capture encounterId from location.state into context if missing
        if (location.state?.encounterId && !patientData?.encounterId) {
          updatePatientData({ encounterId: location.state.encounterId });
        }

        if (source) {
          setMedicalHistory(normalizeHistoryValues(source.medicalHistory));
          setMedicalHistoryOther(source.medicalHistoryOther || "");
          setAlzheimersStatus(source.alzheimersStatus || "");
          setFamilyHistory(normalizeHistoryValues(source.familyHistory));
          setFamilyHistoryOther(source.familyHistoryOther || "");
          setSmoking(toYesNoDisplayValue(source.smoking));
          setYearsSmoking(source.yearsSmoking || "");
          setDrinking(toYesNoDisplayValue(source.drinking));
          setDrinksPerWeek(source.drinksPerWeek || "");
          setExercise(toYesNoDisplayValue(source.exercise));
        }
      } catch (error) {
        console.error("FamilyInfo - Error loading patient data:", error);
      }
    };

    loadAndPrefill();
  }, [location.state, patientData, updatePatientData]);

  const handleBack = () => {
    // Save current form state to context before navigating back
    updatePatientData({
      medicalHistory,
      medicalHistoryOther,
      alzheimersStatus,
      familyHistory,
      familyHistoryOther,
      smoking: toYesNoStoredValue(smoking),
      yearsSmoking,
      drinking: toYesNoStoredValue(drinking),
      drinksPerWeek,
      exercise: toYesNoStoredValue(exercise),
    });
    navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_CHECKIN_WIZARD_GENERAL);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!medicalHistory || medicalHistory.length === 0)
      newErrors.medicalHistory = "Please select at least one option, or choose 'None'.";
    if (!familyHistory || familyHistory.length === 0)
      newErrors.familyHistory = "Please select at least one option, or choose 'None'.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const normalizedSmoking = toYesNoStoredValue(smoking);
      const normalizedDrinking = toYesNoStoredValue(drinking);
      const normalizedExercise = toYesNoStoredValue(exercise);

      // Only send fields the patient API accepts
      const patientUpdates = {
        medicalHistory,
        medicalHistoryOther,
        alzheimersStatus: alzheimersStatus || undefined,
        familyHistory,
        familyHistoryOther,
        smoking: normalizedSmoking,
        yearsSmoking,
        drinking: normalizedDrinking,
        drinksPerWeek,
        exercise: normalizedExercise,
        ethnicity: patientData.ethnicity,
      };
      const checkInPayload = buildEncounterCheckInV2Payload(
        patientUpdates,
        patientData.checkInTime || nowIso(),
      );

      // Update context first
      updatePatientData(patientUpdates);
      console.log("FamilyInfo - Submitting patient updates:", patientUpdates);

      // PATCH patient record
      await updatePatient(patientData.patientId, patientUpdates);
      console.log("FamilyInfo - Patient data updated successfully");

      // Get encounterId - prefer context (which persists via sessionStorage), then location state
      const encounterId = patientData?.encounterId || location.state?.encounterId;
      console.log("🔍 FamilyInfo - Using encounterId:", encounterId);

      if (!encounterId) {
        // Only create a new encounter if we truly don't have one
        console.log("🔍 FamilyInfo - No encounterId found, creating new encounter");
        const newEncounterData = {
          encounterId: uuidv4(),
          patientId: patientData.patientId,
          encounterDateTime: nowIso(),
          encounterType: ENCOUNTER_TYPE.REGULAR,
          checkInStatus: CHECK_IN_STATUS.NOT_CHECKED_IN,
        };
        const createdEncounter = await createEncounter(newEncounterData);
        const newEncounterId = createdEncounter.encounterId || createdEncounter.id;
        console.log("FamilyInfo - Encounter created:", newEncounterId);

        // Store encounterId in context so it's not lost
        updatePatientData({ encounterId: newEncounterId });
        await checkInEncounterV2(newEncounterId, checkInPayload);
        console.log("FamilyInfo - Encounter checked in");
      } else {
        console.log("🔍 FamilyInfo - Checking in existing encounter:", encounterId);
        try {
          await checkInEncounterV2(encounterId, checkInPayload);
          console.log("FamilyInfo - Encounter checked in");
        } catch (checkInErr) {
          console.error("FamilyInfo - Encounter check-in failed:", checkInErr);
          throw checkInErr;
        }
      }

      setComplete(true);
    } catch (error) {
      console.error("FamilyInfo - Error updating patient data:", error);
      setErrors({ submit: "Failed to save data. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };



  const missingFieldLabels = [
    !medicalHistory || medicalHistory.length === 0 ? "Medical History" : null,
    !familyHistory || familyHistory.length === 0 ? "Family History" : null,
  ].filter(Boolean);

  const hasValidationErrors = Object.keys(errors).some((k) => k !== "submit");

  if (complete) {
    return (
      <FocusedFlowPage
        title="You're all set!"
        subtitle="Please return the tablet to the front desk."
        onClose={() => navigate(BIOBANK_ROUTES.PARTICIPANT_CHECKIN)}
        maxWidth={640}
      />
    );
  }

  return (
    <FocusedFlowPage
      title="Medical History, Family History & Life Style"
      onClose={() => navigate(BIOBANK_ROUTES.PARTICIPANT_CHECKIN)}
      maxWidth={860}
      actions={[
        <Button key="back" variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} size="large">
          Back
        </Button>,
        <Button
          key="submit"
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={handleSubmit}
          disabled={isSubmitting}
          size="large"
        >
          {isSubmitting ? "Saving..." : "Submit"}
        </Button>,
      ]}
    >

          <Stack sx={{ px: 4 }} gap={4}>
            {/* ── Returning patient notice ── */}
            {isReturningPatient && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please ask if any medical history or lifestyle information has changed and update accordingly.
              </Alert>
            )}

            {/* ── Error banners ── */}
            {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
            {hasValidationErrors && (
              <Alert severity="error">
                The following required fields are missing:{" "}
                <strong>{missingFieldLabels.join(", ")}</strong>. Please complete them before submitting.
              </Alert>
            )}

            {/* ── Medical History ── */}
            <Box>
              <ChipSelect
                label="Medical History"
                options={HEALTH_CONDITION_OPTIONS}
                selectedValues={medicalHistory}
                onChange={(val) => {
                  setMedicalHistory(val);
                  if (val.length > 0)
                    setErrors((e) => { const n = { ...e }; delete n.medicalHistory; return n; });
                }}
                onBeforeChange={(newValue, oldValue) => {
                  // If "none" was just selected, clear all other selections
                  if (newValue.includes("none") && !oldValue.includes("none")) {
                    newValue.length = 0;
                    newValue.push("none");
                  }
                  // If a disease was selected while "none" is present, remove "none"
                  else if (newValue.includes("none") && newValue.length > 1) {
                    const index = newValue.indexOf("none");
                    newValue.splice(index, 1);
                  }

                  return true;
                }}
                isOptionDisabled={(option, selectedValues) => {
                  // If "none" is selected, disable all other options
                  if (selectedValues.includes("none") && option.value !== "none") {
                    return true;
                  }
                  return false;
                }}
                required
                error={!!errors.medicalHistory}
              />
              {errors.medicalHistory && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.medicalHistory}
                </Typography>
              )}
            </Box>

            <FormControl>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                Other
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please specify any other medical conditions not listed above.
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={medicalHistoryOther}
                onChange={(event) => setMedicalHistoryOther(event.target.value)}
              />
            </FormControl>

            {/* ── Alzheimer's Status ── */}
            <FormControl fullWidth>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                Alzheimer's Status{" "}
                <Typography component="span" variant="body2" color="text.secondary">
                  (Optional)
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Required for AD studies.
              </Typography>
              <Select
                displayEmpty
                value={alzheimersStatus}
                onChange={(e) => setAlzheimersStatus(e.target.value)}
              >
                <MenuItem value="">Select Alzheimer's Status</MenuItem>
                {ALZHEIMERS_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ── Family History ── */}
            <Box>
              <ChipSelect
                label="Family History"
                options={HEALTH_CONDITION_OPTIONS}
                selectedValues={familyHistory}
                onChange={(val) => {
                  setFamilyHistory(val);
                  if (val.length > 0)
                    setErrors((e) => { const n = { ...e }; delete n.familyHistory; return n; });
                }}
                onBeforeChange={(newValue, oldValue) => {
                  // If "none" was just selected, clear all other selections
                  if (newValue.includes("none") && !oldValue.includes("none")) {
                    newValue.length = 0;
                    newValue.push("none");
                  }
                  // If a disease was selected while "none" is present, remove "none"
                  else if (newValue.includes("none") && newValue.length > 1) {
                    const index = newValue.indexOf("none");
                    newValue.splice(index, 1);
                  }

                  return true;
                }}
                isOptionDisabled={(option, selectedValues) => {
                  // If "none" is selected, disable all other options
                  if (selectedValues.includes("none") && option.value !== "none") {
                    return true;
                  }
                  return false;
                }}
                required
                error={!!errors.familyHistory}
              />
              {errors.familyHistory && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.familyHistory}
                </Typography>
              )}
            </Box>

            <FormControl>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                Other
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please specify any other medical conditions not listed above.
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={familyHistoryOther}
                onChange={(event) => setFamilyHistoryOther(event.target.value)}
              />
            </FormControl>
          </Stack>

          <Divider sx={{ mx: 4, my: 4 }} />

          {/* ── Lifestyle Section ── */}
          <Stack sx={{ px: 4, pb: 4 }} gap={4}>
            <Typography variant="h5" sx={{ fontWeight: 700 }} color="primary">
              Lifestyle
            </Typography>

            {/* Smoking */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: errors.smoking ? "error.main" : "text.primary" }}
              >
                Do you smoke?{" "}
                <Typography component="span" variant="body2" color="text.secondary">
                  (Optional)
                </Typography>
              </Typography>
              <RadioGroup
                row
                value={smoking}
                onChange={(e) => {
                  setSmoking(e.target.value);
                  if (e.target.value === "No") setYearsSmoking("");
                  setErrors((err) => { const n = { ...err }; delete n.smoking; return n; });
                }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
              {errors.smoking && (
                <Typography variant="caption" color="error">{errors.smoking}</Typography>
              )}
              {smoking === "Yes" && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    If yes, for how many years (regularly)?
                  </Typography>
                  <Select
                    value={yearsSmoking}
                    onChange={(e) => setYearsSmoking(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Select years</MenuItem>
                    {[...Array(9).keys()].map((num) => (
                      <MenuItem key={num + 1} value={num + 1}>{num + 1}</MenuItem>
                    ))}
                    <MenuItem value="10+">10+</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Drinking */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: errors.drinking ? "error.main" : "text.primary" }}
              >
                Do you drink alcohol?{" "}
                <Typography component="span" variant="body2" color="text.secondary">
                  (Optional)
                </Typography>
              </Typography>
              <RadioGroup
                row
                value={drinking}
                onChange={(e) => {
                  setDrinking(e.target.value);
                  if (e.target.value === "No") setDrinksPerWeek("");
                  setErrors((err) => { const n = { ...err }; delete n.drinking; return n; });
                }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
              {errors.drinking && (
                <Typography variant="caption" color="error">{errors.drinking}</Typography>
              )}
              {drinking === "Yes" && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    If yes, on average, how many alcoholic drinks do you consume per week?
                  </Typography>
                  <Select
                    value={drinksPerWeek}
                    onChange={(e) => setDrinksPerWeek(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Select amount</MenuItem>
                    {[...Array(9).keys()].map((num) => (
                      <MenuItem key={num + 1} value={num + 1}>{num + 1}</MenuItem>
                    ))}
                    <MenuItem value="10+">10+</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Exercise */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: errors.exercise ? "error.main" : "text.primary" }}
              >
                Do you get at least 30 minutes of moderate physical activity, like brisk walking,
                at least 5 days a week?{" "}
                <Typography component="span" variant="body2" color="text.secondary">
                  (Optional)
                </Typography>
              </Typography>
              <RadioGroup
                row
                value={exercise}
                onChange={(e) => {
                  setExercise(e.target.value);
                  setErrors((err) => { const n = { ...err }; delete n.exercise; return n; });
                }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
              {errors.exercise && (
                <Typography variant="caption" color="error">{errors.exercise}</Typography>
              )}
            </Box>

          </Stack>
    </FocusedFlowPage>
  );
};

export default FamilyInfo;
